import {Assert, AssertWarn, GetEntries, Range} from "js-vextensions";
import {AssertUnreachable, dayInMS, hourInMS, minuteInMS, TimeToString} from "web-vcore";
import {ALL_GROUP_NAME, MetricCollector} from "./MetricCollector.js";
import {GetSettingValueFromSession, Session} from "../../Server/DB/sessions/@Session.js";
import {GetSegmentAllText, GetWordsInDreamSegment_Array, JournalEntry, JournalSegment} from "../../Server/DB/journalEntries/@JournalEntry.js";
import {EstimateDreamEndTime} from "../../Server/DB/journalEntries.js";
import {SessionPeriod, SleepCycle} from "../../Server/DB/@Shared/SessionPeriod.js";
import {IsMetaTerm} from "../../Server/DB/entities.js";
import {LucidityLevel, StatView} from "../../Server/DB/statViews/@StatView.js";
import {GetSessionPeriods} from "../../Server/DB/@Shared/SessionPeriodUtils.js";
import {eventTypes_dreamOrSoundQuiz_promptEnder, eventTypes_dreamQuiz} from "../../Engine/LiveSession/SessionEvent.js";
import {GetAnchorStatDayForTargetTime, GetAnchorStatDayOffset, GetStatDayContainingX, GetStatNightContainingX} from "./Helpers.js";
import {MapObject} from "../../Server/DB/mapObjects/@MapObject.js";
import {CreateAccessor, RunInAction} from "mobx-graphlink";
import {StatsGrouping, StatsXType, StatsXType_tickStepInfos, StatsYType, StatsYType_entries} from "../../Server/DB/statViews/@StatTypes.js";
import {SmoothingType} from "../../Store/main/tools/journey.js";
import {store} from "../../Store/index.js";
import {EngineConfig} from "../../Server/DB/engineConfigs/@EngineConfig.js";
import {EstimateJournalEntryStartTime} from "../../Server/DB/@Shared/JournalTimeEstimates.js";

export type AggregationMeta = ReturnType<typeof CreateAggregationMeta>;
// this accessor func creates a caching-point, so the returned object can be safely passed as argument without breaking downstream cache
export const CreateAggregationMeta = CreateAccessor((
	// from ui-state (as main case)
	xType: StatsXType,
	yType: StatsYType,
	combined_yTypes: StatsYType[],
	grouping: StatsGrouping,
	dreamMatcher_text: string,
	maxMemoryPromptRecallGap: number|n,
	lucidityLevels: LucidityLevel[]|n,
	dateRange_enabled: boolean,
	dateRange_min: number|n,
	dateRange_max: number|n,
	xAxis_stepOverride: number|n,
)=>{
	Assert(GetEntries(StatsGrouping).map(a=>a.value).includes(grouping), `Invalid grouping! Got:${grouping}`);
	return {
		xType, yType, combined_yTypes, grouping, dreamMatcher_text, maxMemoryPromptRecallGap, lucidityLevels,
		dateRange_enabled, dateRange_min, dateRange_max, xAxis_stepOverride,
	};
});

export class AggregationData {
	constructor(xTicks_initial: number[]) {
		this.xTicks = xTicks_initial;
	}

	xTicks: number[];

	// groups
	groups_variants = [] as string[];
	groups_alarmSequences = [] as string[];
	groups_alarmDelays = [] as string[];
	groups_globalVolumeMods = [] as string[];
	groups_globalAlarmRestartIntervalMods = [] as string[];
	groups_memoryPalaceTargets = [] as string[];

	// samples
	sessionsForEachXValue = new Map<number, Session[]>();
	//dreamsForEachXValue = new Map<number, JournalEntry[]>();
	segmentsForEachXValue = new Map<number, JournalSegment[]>();
	//compositeSessionForEachXValue = new Map<number, {proHits: number, conHits: number}>();
	segmentExistenceSamples = new MetricCollector<number>();
	segmentIsLucidSamples = new MetricCollector<number>();
	segmentTechExecutionSamples = new MetricCollector<number|null>();
	segmentIsMatchSamples = new MetricCollector<boolean>();
	segmentTermCountSamples = new MetricCollector<number>();
	responseTimeSamples = new MetricCollector<number>();
	visualizationPromptBlockSamples = new MetricCollector<number>();
	quizPromptHasHitSamples = new MetricCollector<boolean>();
	quizTryIsHitSamples = new MetricCollector<boolean>();
	quizFirstTryIsHitSamples = new MetricCollector<boolean>();
	quizTimeTillSuccessSamples = new MetricCollector<number>();
	memoryPalacePromptRecalledSamples = new MetricCollector<boolean>();
	memoryPalacePromptDecodeTimeSamples = new MetricCollector<number>();
	memoryPalacePromptEncodeTimeSamples = new MetricCollector<number>();
	linkVoicingExistenceSamples = new MetricCollector<boolean>();
	linkVisualizationExistenceSamples = new MetricCollector<boolean>();
	realityCheckExistenceSamples = new MetricCollector<boolean>();
	frameDisplayExistenceSamples = new MetricCollector<boolean>();

	// plain data (not really aggregated, just stored in the same data-package)
	mapObjects_all = [] as MapObject[];

	// for debugging
	groupCycles = new Map<string, SleepCycle[]>();
	groupDreamCycles = new Map<string, SleepCycle[]>(); // cycles (of the night/non-day type), per group
	groupDreamDays = new Map<string, number[]>(); // anchor stat-days (of dream-containing session-periods), per group
}

/*export function JStatsUI_GetAggregationData_Standard() {
	const uiState = store.main.stats;
	const xTicks = JStatsUI_GetTicks(uiState);
	const sessions = GetSessions(MeID());
	const dreams = GetJournalEntries(MeID());
	const meta = CreateAggregationMeta(
		uiState.xType, uiState.yType, uiState.combined_yTypes, uiState.grouping,
		uiState.dateRange_enabled, uiState.dateRange_min, uiState.dateRange_max,
		xTicks,
	);
	const aggregationData = JStatsUI_GetAggregationData(xTicks, sessions, dreams, meta);
	return aggregationData;
}*/

/** Result is in minutes. */
export function GetAlarmDelayCandidatesFromEngineConfig(config: EngineConfig) {
	const jConf = config["journey"] ?? {};
	const aConf = config.alarms ?? {};
	const validNums = (...possibleNums: any[])=>possibleNums.filter(a=>a != null && typeof a == "number" && !isNaN(a));

	const delaysInMS = [] as number[];
	if (validNums(jConf.resleep_waitDuration)) {
		delaysInMS.push(jConf.resleep_waitDuration);
	}
	if (validNums(jConf.resleep_waitDuration_min, jConf.resleep_waitDuration_max, jConf.resleep_waitDuration_step)) {
		delaysInMS.push(...Range(jConf.resleep_waitDuration_min, jConf.resleep_waitDuration_max, jConf.resleep_waitDuration_step));
	}
	if (jConf.alarmDelay_pool != null && validNums(...jConf.alarmDelay_pool)) {
		delaysInMS.push(...jConf.alarmDelay_pool.map(a=>a * minuteInMS));
	}
	if (aConf.alarmDelay_pool != null && validNums(...aConf.alarmDelay_pool)) {
		delaysInMS.push(...aConf.alarmDelay_pool.map(a=>a * minuteInMS));
	}
	return delaysInMS.map(a=>(a / minuteInMS).RoundTo(1)).Distinct();
}

export function JStatsUI_GetAggregationData(
	sessions_raw: Session[], dreams_raw: JournalEntry[], mapObjects_raw: MapObject[],
	// from ui-state (as main case)
	meta: AggregationMeta, xTicks_initial: number[], groupRenames: {from: string, to: string}[],
): AggregationData {
	const sessions = sessions_raw.filter(session=>{
		if (meta.dateRange_enabled && meta.dateRange_min && session.startTime < meta.dateRange_min) return false;
		if (meta.dateRange_enabled && meta.dateRange_max && session.endTime > meta.dateRange_max) return false;
		return true;
	});
	const dreams = dreams_raw.filter(dream=>{
		const startTime = EstimateJournalEntryStartTime(dream);
		const endTime = EstimateDreamEndTime(dream);
		if (meta.dateRange_enabled && meta.dateRange_min && startTime < meta.dateRange_min) return false;
		if (meta.dateRange_enabled && meta.dateRange_max && endTime && endTime > meta.dateRange_max) return false;
		return true;
	});

	const sessionPeriods = GetSessionPeriods(dreams, sessions);

	/*const sessionPeriodsBasic = GetSessionPeriods_Me();
	Assert(sessionPeriodsBasic.length == sessionPeriods.length, "Test1");
	console.log("Has target period1:", sessionPeriods.Any(a=>a.startTime == 1732176001000));
	console.log("Has target period2:", sessionPeriodsBasic.Any(a=>a.startTime == 1732176001000));*/

	const allSessions = sessionPeriods.SelectMany(a=>a.sessions);
	/*const allCycles = sessionPeriods.SelectMany(a=>a.sleepCycles);
	const getPeriodForSession = (session: Session)=>sessionPeriods.find(a=>a.sessions.includes(session))!;
	const getPeriodForDream = (entry: JournalEntry)=>sessionPeriods.find(a=>a.journalEntries.includes(entry))!;
	const getPeriodForSegment = (segment: JournalSegment)=>sessionPeriods.find(a=>a.journalSegments.includes(segment))!;
	const getPeriodForCycle = (cycle: SleepCycle)=>sessionPeriods.find(a=>a.sleepCycles.includes(cycle))!;*/

	const data = new AggregationData(xTicks_initial);
	data.mapObjects_all = mapObjects_raw;

	const periodGroups = new Map<SessionPeriod, string>();
	const groupPeriods = new Map<string, SessionPeriod[]>();
	for (const period of sessionPeriods) {
		const group = (()=>{
			const group_byGroupType = {
				//none: ALL_GROUP_NAME,
				variant: period.sessions.map(a=>a.variantName).find(a=>a) ?? "[base]",
				alarmSequence: period.sessions.map(a=>a.alarms_mainSequence).find(a=>a) ?? "[none]",
				alarmDelay: (()=>{
					// if a session-period...
					// * has no actual dream journal-segments
					// * OR, it has no actual sleep-cycles (with an alarm-delay AND dream journal-segments associated with it)
					// ...then always consider its alarm-delay group as "NaN".
					// (we don't want these session-periods messing up the per-group stats)
					/*const relevantDreamSegments = period.journalSegments.filter(a=>a.wakeTime == null);
					const relevantSleepCycles = period.sleepCycles.filter(a=>{
						if (a.cycleNumber == 0 || a.alarmDelay == null || isNaN(a.alarmDelay)) return false;
						if (a.associatedDreamSegments.filter(b=>b.wakeTime == null).length == 0) return false;
						return true;
					});
					if (relevantDreamSegments.length == 0 || relevantSleepCycles.length == 0) return "NaN";*/

					return period.sleepCycles.filter(a=>a.cycleNumber != 0).map(a=>a.alarmDelay).find(a=>a)?.toString() ?? "NaN";
				})(),
				volumeMod: period.sessions.map(a=>GetSettingValueFromSession<number>(a, b=>b.general.globalVolumeMultiplier, 1)).find(a=>a)?.toString() ?? "NaN",
				alarmRestartIntervalMod: period.sessions.map(a=>GetSettingValueFromSession<number>(a, b=>b.general.globalAlarmRestartIntervalMultiplier, 1)).find(a=>a)?.toString() ?? "NaN",
			};
			// apply user-defined group renames (the removal of groups with the empty name of "" is handled later, in the chart-creation code -- specifically, in StatsChartOptions.ts)
			for (const [groupType, groupName_orig] of Object.entries(group_byGroupType)) {
				group_byGroupType[groupType] = groupRenames.find(a=>a.from == groupName_orig)?.to ?? groupName_orig;
			}

			data.groups_variants.push(group_byGroupType.variant);
			data.groups_alarmSequences.push(group_byGroupType.alarmSequence);
			data.groups_alarmDelays.push(group_byGroupType.alarmDelay);
			data.groups_globalVolumeMods.push(group_byGroupType.volumeMod);
			data.groups_globalAlarmRestartIntervalMods.push(group_byGroupType.alarmRestartIntervalMod);

			if (meta.grouping == StatsGrouping.none) return ALL_GROUP_NAME;
			if (meta.grouping == StatsGrouping.variant) return group_byGroupType.variant;
			if (meta.grouping == StatsGrouping.alarmSequence) return group_byGroupType.alarmSequence;
			if (meta.grouping == StatsGrouping.alarmDelay) return group_byGroupType.alarmDelay;
			if (meta.grouping == StatsGrouping.volumeMod) return group_byGroupType.volumeMod;
			if (meta.grouping == StatsGrouping.alarmRestartIntervalMod) return group_byGroupType.alarmRestartIntervalMod;
			// for the grouping-types below, grouping applies at sample-level, not session-period level; so return ALL_GROUP_NAME here
			if (meta.grouping == StatsGrouping.memoryPalaceTarget) return ALL_GROUP_NAME;
			AssertUnreachable(meta.grouping);
		})();
		periodGroups.set(period, group);
		groupPeriods.set(group, (groupPeriods.get(group) ?? []).concat(period));
	}

	// remove duplicates, in "groups found of type-x" arrays
	data.groups_variants = data.groups_variants.Distinct();
	data.groups_alarmSequences = data.groups_alarmSequences.Distinct();
	data.groups_alarmDelays = data.groups_alarmDelays.Distinct();
	data.groups_globalVolumeMods = data.groups_globalVolumeMods.Distinct();
	data.groups_globalAlarmRestartIntervalMods = data.groups_globalAlarmRestartIntervalMods.Distinct();
	// for the grouping-types below, find all values across all sessions/events in data-set (since grouped later, at sample-level)
	data.groups_memoryPalaceTargets = sessionPeriods
		.flatMap(a=>{
			const allEvents = a.eventGroups.flatMap(b=>b.events);
			const mpTargetFindEvents = allEvents.filter(b=>b.type == "MemoryPalace.PromptPlay" && b.memoryPalace_promptPlay?.type != "move" && b.memoryPalace_promptPlay?.target != null);
			return mpTargetFindEvents.map(b=>b.memoryPalace_promptPlay!.target!);
		})
		.Distinct()
		// since memory-palace-target group values are added here, we have to apply group-renames here as well
		.map(a=>groupRenames.find(b=>b.from == a)?.to ?? a).Distinct();

	data.groupCycles = [...groupPeriods].ToMap(a=>a[0], a=>a[1].SelectMany(b=>b.sleepCycles));
	const isDreamCycle = (cycle: SleepCycle)=>{
		return cycle.associatedDreamSegments.filter(a=>a.wakeTime == null).length > 0 || cycle.syntheticallyAddedSleepCycle;
		//if (cycle.associatedSessions.length == 0 && cycle.associatedDreamSegments.some(a=>a.wakeTime == null)) return true;
		/*if (cycle.associatedDreamSegments.some(a=>a.wakeTime == null)) return true;
		if (cycle.associatedSessions.Any(a=>a.launchType == "night")) {
			debugger;
			return true;
		}
		return false;*/
	};
	data.groupDreamCycles = [...groupPeriods].ToMap(a=>a[0], a=>a[1].SelectMany(b=>b.sleepCycles.filter(isDreamCycle)));

	const isDreamPeriod = (period: SessionPeriod)=>period.journalSegments.some(a=>a.wakeTime == null);
	const getPeriodAnchorStatDay = (period: SessionPeriod)=>period.DayInfo().anchorStatDay;
	data.groupDreamDays = [...groupPeriods].ToMap(
		a=>a[0],
		a=>a[1].filter(isDreamPeriod).map(getPeriodAnchorStatDay).Distinct().OrderBy(b=>b),
	);

	// optimization: find all frame-displays now, without regard for grouping (for the "frameDisplays_sum_last24Hours" x-type)
	const frameDisplays = sessions.flatMap(a=>a.events.filter(b=>b.type == "VR.ImageFrameDisplay"));

	const dreamCyclesProcessedPerGroup = new Map<string, number>();
	for (const period of sessionPeriods) {
		const group = periodGroups.get(period)!;

		const xTicksInfo = StatsXType_tickStepInfos.get(meta.xType);
		for (const cycle of period.sleepCycles) {
			let xTick = (()=>{
				// x-or-y-types
				if (meta.xType == "dreamSegments_sum") return period.journalSegments.filter(a=>a.wakeTime == null).length; // x-tick from: sleep-period/night
				if (meta.xType == "lucids_sum") return period.journalSegments.filter(a=>a.lucid).length; // x-tick from: sleep-period/night
				if (meta.xType == "responseTime") return cycle.responseTime; // x-tick from: cycle [rounded to power of 10, as per stat-type's xTicks_stepOverride]
				if (meta.xType == "linkVoicings_sum") return period.eventGroups.map(a=>a.events.filter(b=>b.type == "ConceptLink.TargetVoiced").length).Sum(); // x-tick from: sleep-period/night
				if (meta.xType == "linkVisualizations_sum") return period.eventGroups.map(a=>a.events.filter(b=>b.type == "ConceptLink.TargetVisualized").length).Sum(); // x-tick from: sleep-period/night
				if (meta.xType == "realityChecks_sum") return period.eventGroups.map(a=>a.events.filter(b=>b.type == "RealityCheck.ReminderHit").length).Sum(); // x-tick from: sleep-period/night
				if (meta.xType == "frameDisplays_sum") return period.eventGroups.map(a=>a.events.filter(b=>b.type == "VR.ImageFrameDisplay").length).Sum(); // x-tick from: sleep-period/night
				//if (meta.xType == "frameDisplays_sum") return cycle.associatedEventGroups.map(a=>a.events.filter(b=>b.type == "VR.ImageFrameDisplay").length).Sum(); // TEST

				// x-types
				if (meta.xType == "showAll") return 0;
				if (meta.xType == "dayOffset") {
					if (period.sleepStartTime) {
						return GetAnchorStatDayOffset(period.sleepStartTime, "night");
					}
					// if sleep-start-time is null, session-period is considered a "day" session-period; so derive stat-day-offset simply by reading the session-period's start-time as a "day" target-time
					return GetAnchorStatDayOffset(period.startTime, "day");
				}
				if (meta.xType == "cycleInGroup") {
					if (isDreamCycle(cycle)) {
						const dreamCycleNumberInGroup = (dreamCyclesProcessedPerGroup.get(group) ?? 0) + 1;
						dreamCyclesProcessedPerGroup.set(group, dreamCycleNumberInGroup);
						return -dreamCycleNumberInGroup.Distance(data.groupDreamCycles.get(group)!.length);
					}
					return null;
				}
				if (meta.xType == "dayInGroup") {
					const anchorStatDay = getPeriodAnchorStatDay(period);
					const groupDays = data.groupDreamDays.get(group)!;
					const dayIndex = groupDays.indexOf(anchorStatDay);
					if (dayIndex == -1) return null;
					return -dayIndex.Distance(groupDays.length - 1);
				}
				if (meta.xType == "cycleInNight") return cycle.cycleNumber;
				if (meta.xType == "startHour") return new Date(period.startTime).getHours();
				if (meta.xType == "endHour") return period.endTime ? new Date(period.endTime).getHours() : -1;
				if (meta.xType == "cycle1EndHour") {
					/*if (period.sleepCycles[0].endTime && new Date(period.sleepCycles[0].endTime).getHours() == 10) {
						console.log(`Got weird case. @startHour:`, new Date(period.startTime).getHours(), "@period:", period, "@cycle1:", period.sleepCycles[0]);
					}*/
					return period.sleepCycles[0].endTime ? new Date(period.sleepCycles[0].endTime).getHours() : -1;
				}
				if (meta.xType == "cycleLastStartHour") return period.sleepCycles.LastOrX()?.startTime ? new Date(period.sleepCycles.LastOrX()!.startTime).getHours() : -1;
				if (meta.xType == "frameDisplays_sum_last24Hours") { // x-tick from: cycle
					if (cycle.endTime == null) return -100; // if unusual cycle that somehow don't have an end-time discerned, say has -100 recent frame-displays
					return frameDisplays.filter(a=>{
						// ignore frame-displays from after the cycle's end-time (ie. after the wake-time)
						if (a.date > cycle.endTime!) return false;
						const delayFromFrameDisplayToCycleEnd = cycle.endTime! - a.date;
						// ignore frame-displays that are too far in the past (ie. more than 2 hours before the cycle's end-time)
						if (delayFromFrameDisplayToCycleEnd > 24 * hourInMS) return false;
						return true;
					}).length;
				}
				if (meta.xType == "frameDisplays_sincePrevNight_sum" || meta.xType == "memoryPrompts_sincePrevNight_sum" || meta.xType == "visPrompts_sincePrevNight_sum") { // x-tick from: cycle
					const anchorStatDay_min = GetAnchorStatDayForTargetTime(period.startTime, period.DayInfo().type);
					const periodsIncludedAtLeastPartially = sessionPeriods.filter(a=>a.DayInfo().anchorStatDay >= anchorStatDay_min && a.startTime <= period.startTime);
					const cyclesIncluded = periodsIncludedAtLeastPartially.flatMap(a=>{
						// for own period, exclude cycles after the currently-being-processed cycle
						if (a == period) return a.sleepCycles.filter(b=>b.startTime <= cycle.startTime);
						return a.sleepCycles;
					});

					if (meta.xType == "frameDisplays_sincePrevNight_sum") {
						const frameDisplayEventsIncluded = cyclesIncluded.flatMap(a=>a.associatedEventGroups).flatMap(a=>a.events).filter(a=>a.type == "VR.ImageFrameDisplay");
						return frameDisplayEventsIncluded.length;
					}
					if (meta.xType == "memoryPrompts_sincePrevNight_sum") {
						const mpPromptPlayEventsIncluded = cyclesIncluded.flatMap(a=>a.associatedEventGroups).flatMap(a=>a.events).filter(a=>a.type == "MemoryPalace.PromptPlay");
						return mpPromptPlayEventsIncluded.length;
					}
					if (meta.xType == "visPrompts_sincePrevNight_sum") {
						const cyclesIncluded_events = cyclesIncluded.flatMap(a=>a.associatedEventGroups).flatMap(a=>a.events);
						let totalVisPromptsInPeriod = 0;
						for (const event of cyclesIncluded_events) {
							if (event.type == "Visualization.StartPrompting") {
								const futureEventsInCycle = cyclesIncluded_events.slice(cyclesIncluded_events.indexOf(event) + 1);
								const enderEvent = futureEventsInCycle.find(a=>a.type == "Visualization.StartPrompting" || a.type == "Visualization.StopPrompting" || a.type == "General.SessionEnd");
								Assert(enderEvent, `No ender event found for Visualization.StartPrompting event @${TimeToString(event.date)} in session ${cycle.associatedSessions.map(a=>a.id).join(", ")}`);
								const promptBlockDuration = enderEvent.date - event.date;
								const promptInterval = event.visualization_startPrompting!.promptInterval;
								const promptsInThisPromptBlock = Math.floor(promptBlockDuration / promptInterval!);
								totalVisPromptsInPeriod += promptsInThisPromptBlock;
							}
						}
						return totalVisPromptsInPeriod;
					}
				}

				AssertUnreachable(meta.xType);
			})();
			// if xTick is null, it means this cycle is not relevant for the current xType, so skip
			if (xTick == null) continue;
			/*if (period.journalSegments.Any(a=>a.lucid) && group != "NaN" && meta.xType == StatsXType.cycleInGroup) {
				debugger;
			}*/

			const stepOverride = meta.xAxis_stepOverride ?? xTicksInfo?.xTicks_stepOverride;
			if (stepOverride != null) {
				xTick = xTick.RoundTo(stepOverride);
			}
			// todo: handle conflict between this (which wants to be the final say) and user-provided step-override
			if (xTicksInfo?.xTicks_override != null) {
				xTick = xTicksInfo.xTicks_override.OrderBy(a=>a.Distance(xTick!))[0];
			}

			// if x-ticks array is not hard-coded, then ensure that x-ticks array is large enough to cover this x-value
			if (xTicksInfo?.xTicks_override == null && !xTicksInfo?.xTicks_lockToInitial && !data.xTicks.Contains(xTick)) {
				/*const insertPos = meta.xTicks.findIndex(a=>a > xTick) ?? meta.xTicks.length;
				meta.xTicks.Insert(insertPos, xTick);*/
				const newMin = (data.xTicks.FirstOrX() ?? xTick).KeepAtMost(xTick);
				const newMax = (data.xTicks.LastOrX() ?? xTick).KeepAtLeast(xTick);
				// clear and re-populate the array (rather than re-assign), for peace of mind that we aren't storing a ref to an outdated version of the array elsewhere
				data.xTicks.Clear();
				data.xTicks.push(...Range(newMin, newMax, stepOverride ?? 1));
			}

			IntegrateSleepCycleIntoAggregationData(data, meta, cycle, period, xTick, group);
		}
	}

	return data;
}

function IntegrateSleepCycleIntoAggregationData(
	data: AggregationData, meta: AggregationMeta, cycle: SleepCycle, period: SessionPeriod,
	xTick: number, group: string,
) {
	for (const session of cycle.associatedSessions) {
		data.sessionsForEachXValue.set(xTick, (data.sessionsForEachXValue.get(xTick) ?? []).concat(session));
	}

	// cycle with cycle-number 0 are "fake" cycles, used to represent eg. daytime session-periods
	if (cycle.cycleNumber != 0) {
		// todo: maybe change this to consider multiple sessions that cover the same sleep (ie. journal-entry) period, as part of one "composite session"
		data.responseTimeSamples.AddSample(xTick, cycle.responseTime, cycle.alarmDelay.toString());
	}

	for (const [index, eventGroup] of cycle.associatedEventGroups.entries()) {
		const priorEventGroups = cycle.associatedEventGroups.slice(0, index);
		const priorEventGroupEvents = priorEventGroups.SelectMany(a=>a.events);

		for (const [event_index, event] of eventGroup.events.entries()) {
			const priorEvents = priorEventGroupEvents.concat(eventGroup.events.slice(0, event_index));

			// dream-quiz events
			if (event.type == "Visualization.StartPrompting") {
				const futureEventsInCycle = eventGroup.events.slice(event_index + 1).concat(cycle.associatedEventGroups.slice(index + 1).SelectMany(a=>a.events));
				const enderEvent = futureEventsInCycle.find(a=>a.type == "Visualization.StartPrompting" || a.type == "Visualization.StopPrompting" || a.type == "General.SessionEnd");
				Assert(enderEvent, `No ender event found for Visualization.StartPrompting event @${TimeToString(event.date)} in session ${cycle.associatedSessions.map(a=>a.id).join(", ")}`);
				const promptBlockDuration = enderEvent.date - event.date;
				const promptInterval = event.visualization_startPrompting!.promptInterval;
				const promptsInThisPromptBlock = Math.floor(promptBlockDuration / promptInterval!);
				data.visualizationPromptBlockSamples.AddSample(xTick, promptsInThisPromptBlock, group);
			} else if (event.type == "DreamQuiz.TargetHit") {
				data.quizPromptHasHitSamples.AddSample(xTick, true, group);
				data.quizTryIsHitSamples.AddSample(xTick, true, group);
				const dreamQuiz_lastHitOrMissOrGiveUp = priorEvents.findLast(a=>eventTypes_dreamQuiz.includes(a.type));
				const isFirstTry = dreamQuiz_lastHitOrMissOrGiveUp?.type != "DreamQuiz.TargetMiss";
				if (isFirstTry) {
					data.quizFirstTryIsHitSamples.AddSample(xTick, true, group);
				}

				const lastIndexBeforeQuizEventsBlock = priorEvents.findLastIndex(a=>!eventTypes_dreamQuiz.includes(a.type));
				const priorEventsInQuizEventsBlock = lastIndexBeforeQuizEventsBlock == -1 ? [] :
					priorEvents.slice(lastIndexBeforeQuizEventsBlock + 1);
				const quiz_lastHitOrGiveUpEarlierInBlock = priorEventsInQuizEventsBlock.findLast(a=>eventTypes_dreamOrSoundQuiz_promptEnder.includes(a.type));
				if (quiz_lastHitOrGiveUpEarlierInBlock) {
					const timeTillSuccess = event.date - quiz_lastHitOrGiveUpEarlierInBlock.date;
					// hard-coded: if a quiz-prompt takes over 60s to solve, assume it was interrupted and discard it
					if (timeTillSuccess <= 60000) {
						data.quizTimeTillSuccessSamples.AddSample(xTick, timeTillSuccess, group);
					}
				}
			} else if (event.type == "DreamQuiz.TargetMiss") {
				data.quizTryIsHitSamples.AddSample(xTick, false, group);
				const dreamQuiz_lastHitOrMissOrGiveUp = priorEvents.findLast(a=>eventTypes_dreamQuiz.includes(a.type));
				const isFirstTry = dreamQuiz_lastHitOrMissOrGiveUp?.type != "DreamQuiz.TargetMiss";
				if (isFirstTry) {
					data.quizFirstTryIsHitSamples.AddSample(xTick, false, group);
				}
			} else if (event.type == "DreamQuiz.TargetGiveUp") {
				data.quizPromptHasHitSamples.AddSample(xTick, false, group);
			} else if (event.type == "ConceptLink.TargetVoiced") {
				data.linkVoicingExistenceSamples.AddSample(xTick, true, group);
			} else if (event.type == "ConceptLink.TargetVisualized") {
				data.linkVisualizationExistenceSamples.AddSample(xTick, true, group);
			} else if (event.type == "RealityCheck.ReminderHit") {
				data.realityCheckExistenceSamples.AddSample(xTick, true, group);
			} else if (event.type == "VR.ImageFrameDisplay") {
				data.frameDisplayExistenceSamples.AddSample(xTick, true, group);
			} else if (event.type == "MemoryPalace.PromptPlay" && event.memoryPalace_promptPlay?.type != "move") { // atm only include recall events (else recall-% skewed)
				const futureEvents = eventGroup.events.slice(event_index + 1).concat(cycle.associatedEventGroups.slice(index + 1).SelectMany(a=>a.events));
				const enderEvent = futureEvents.find(a=>a.type == "MemoryPalace.RecallResult" || a.type == "MemoryPalace.DecodeFailed" || a.type == "MemoryPalace.PromptPlay");
				const wasRecalled =
					enderEvent?.memoryPalace_recallResult ? enderEvent.memoryPalace_recallResult.decodeSuccess :
					enderEvent?.type == "MemoryPalace.DecodeFailed" ? false :
					true;

				// sync with copy below
				const encodeToDecodeDelay = enderEvent?.memoryPalace_recallResult?.encodeToDecodeDelay;
				const maxRecallGap = meta.maxMemoryPromptRecallGap ?? 0;
				if (maxRecallGap > 0) {
					if (encodeToDecodeDelay == null) continue; // if unknown, exclude event (can't verify it's within the limit)
					if (encodeToDecodeDelay > maxRecallGap * minuteInMS) continue; // if too large, exclude event
				}

				let group_final = group;
				// if grouping type is memory-palace-target, we override the group here (it's a special case, of sample-level grouping)
				if (meta.grouping == StatsGrouping.memoryPalaceTarget) {
					group_final = enderEvent?.memoryPalace_recallResult?.target ?? event.memoryPalace_promptPlay?.target ?? group_final;
				}
				data.memoryPalacePromptRecalledSamples.AddSample(xTick, wasRecalled, group_final);
			} else if (event.type == "MemoryPalace.RecallResult") {
				// sync with copy above
				const encodeToDecodeDelay = event.memoryPalace_recallResult?.encodeToDecodeDelay;
				const maxRecallGap = meta.maxMemoryPromptRecallGap ?? 0;
				if (maxRecallGap > 0) {
					if (encodeToDecodeDelay == null) continue; // if unknown, exclude event (can't verify it's within the limit)
					if (encodeToDecodeDelay > maxRecallGap * minuteInMS) continue; // if too large, exclude event
				}

				const sub = event.memoryPalace_recallResult;
				Assert(sub != null, "Expected non-null memoryPalace_recallResult on MemoryPalace.RecallResult event.");
				if (sub.decodeAttemptDuration != null) {
					data.memoryPalacePromptDecodeTimeSamples.AddSample(xTick, sub.decodeAttemptDuration, group);
				}
				if (sub.encodeNextDuration != null) {
					data.memoryPalacePromptEncodeTimeSamples.AddSample(xTick, sub.encodeNextDuration, group);
				}
			}
		}
	}

	/*if (xTick >= -10) {
		const frameDisplays = period.eventGroups.flatMap(a=>a.events.filter(b=>b.type == "VR.ImageFrameDisplay"));
		console.log(`X-Tick(${xTick}) frameDisplays(${frameDisplays.length}) sessionPeriodIDs(${period.sessions.map(a=>a.id)}) @periodStartTime:${TimeToString(period.startTime)} @periodEndTime:${period.endTime ? TimeToString(period.endTime) : "n/a"}`);
	}*/

	for (const segment of cycle.associatedDreamSegments) {
		IntegrateJournalSegmentIntoAggregationData(data, meta, segment, period, xTick, group);
	}

	// add empty/zeroed data for this cycle (in the "segments" data slots), since otherwise they show up as nulls/zero the line-graphs (relevant when using smoothing, since looks jarring and messes up eg. lucidity-rate stats)
	if (cycle.syntheticallyAddedSleepCycle) {
		//data.segmentsForEachXValue.set(xTick, (data.segmentsForEachXValue.get(xTick) ?? []).concat(cycle.associatedDreamSegments));
		data.segmentExistenceSamples.AddSample(xTick, 1, group); // we'll say "1", as though there were a dream-segment entry here
		data.segmentIsLucidSamples.AddSample(xTick, 0, group);
		data.segmentIsMatchSamples.AddSample(xTick, false, group);
		data.segmentTermCountSamples.AddSample(xTick, 0, group);
	}
}

function IntegrateJournalSegmentIntoAggregationData(
	data: AggregationData, meta: AggregationMeta, segment: JournalSegment, period: SessionPeriod,
	xTick: number, group: string,
) {
	if (segment.wakeTime != null) return; // only dream/non-wake segments are relevant atm
	const lucidityValue =
		segment.lucid ? 1 :
		segment.semiLucid ? .25 :
		0;
	if (meta.lucidityLevels != null) {
		if (lucidityValue == 1 && !meta.lucidityLevels.includes("lucid")) return;
		if (lucidityValue == .25 && !meta.lucidityLevels.includes("semiLucid")) return;
		if (lucidityValue == 0 && !meta.lucidityLevels.includes("nonLucid")) return;
	}
	//const time = EstimateTimeOfSegment(dream, segment);

	// optimization (and range-applying if using smoothing): skip segments that are too far outside the x-values range
	// commented; this cutoff would change the data lines (when using smoothing) depending on how large of a view window, which is confusing
	/*const xValues_min = meta.xTicks.First();
	const xValues_max = meta.xTicks.Last();
	if (xTick < xValues_min || xTick > xValues_max) return;*/

	data.segmentsForEachXValue.set(xTick, (data.segmentsForEachXValue.get(xTick) ?? []).concat(segment));

	// todo: probably remove segmentsForEachXValue, and just use segmentExistenceSamples directly
	data.segmentExistenceSamples.AddSample(xTick, 1, group);

	data.segmentIsLucidSamples.AddSample(xTick, lucidityValue, group);
	data.segmentTechExecutionSamples.AddSample(xTick, segment.techExecution_preSleep ?? null, group);

	const textHasMatch = (text: string, matcherText: string)=>{
		if (matcherText.startsWith("/") && matcherText.endsWith("/")) {
			const regex = new RegExp(matcherText.slice(1, -1), "i");
			return regex.test(text);
		}
		return text.toLowerCase().includes(matcherText.toLowerCase());
	};
	const isMatch = textHasMatch(GetSegmentAllText(segment), meta.dreamMatcher_text);
	data.segmentIsMatchSamples.AddSample(xTick, isMatch, group);

	const yTypes = meta.yType == "combined" ? meta.combined_yTypes : [meta.yType];
	const segmentTerms = GetWordsInDreamSegment_Array(segment, yTypes.ContainsAny("termsInShortText", "termsInShortText_sum") ? "shortText" : "longText", false, false);
	const validSegmentTerms = segmentTerms.filter(a=>!IsMetaTerm(a));
	data.segmentTermCountSamples.AddSample(xTick, validSegmentTerms.length, group);
}

export function GetYValuesForYType(view: StatView, xTicks: number[], xTicks_extendedForSmoothing: number[], aggregationData: AggregationData, yType: StatsYType, group: string) {
	const {
		segmentExistenceSamples, segmentIsLucidSamples, segmentIsMatchSamples, segmentTermCountSamples, responseTimeSamples,
		visualizationPromptBlockSamples, quizPromptHasHitSamples, quizTryIsHitSamples, quizFirstTryIsHitSamples, quizTimeTillSuccessSamples,
		memoryPalacePromptRecalledSamples, memoryPalacePromptDecodeTimeSamples, memoryPalacePromptEncodeTimeSamples,
		linkVoicingExistenceSamples, linkVisualizationExistenceSamples, realityCheckExistenceSamples, frameDisplayExistenceSamples,
	} = aggregationData;

	const getAvgOfMidXPercent = (
		sampleCollector: MetricCollector<number>, xValue: number, group: string,
		resultIfNoSamples: number|null, nonNullResultTransform = (result: number)=>result,
		middleKeepPercent_override?: number,
	)=>{
		resultIfNoSamples = null; // maybe temp; I actually like null to always be used for "no samples"
		const middleKeepPercent = middleKeepPercent_override ?? view.middleKeepPercent;

		const samples = sampleCollector.GetSampleValues(xValue, group);
		if (samples.length == 0) return resultIfNoSamples;
		const samples_sorted = samples.OrderBy(a=>a);

		// special case: if middle-keep-% is 0, interpret that as meaning "keep just the median value"
		if (middleKeepPercent == 0) {
			return nonNullResultTransform(samples_sorted.Median());
		}
		const percentToTrimAtEachExtreme = (1 - (middleKeepPercent ?? 1)) / 2;
		const samples_sorted_toKeep_startIndex = Math.floor(samples_sorted.length * percentToTrimAtEachExtreme);
		const samples_sorted_toKeep = samples.slice(samples_sorted_toKeep_startIndex, samples_sorted.length - samples_sorted_toKeep_startIndex);
		return nonNullResultTransform(samples_sorted_toKeep.Average());
	};
	const getSum = (sampleCollector: MetricCollector<number>, xValue: number, group: string, resultIfNoSamples: number|null)=>{
		resultIfNoSamples = null; // maybe temp; I actually like null to always be used for "no samples"

		const samples = sampleCollector.GetSampleValues(xValue, group);
		if (samples.length == 0) return resultIfNoSamples;
		return samples.Sum();
	}
	const getSum_boolean = (sampleCollector: MetricCollector<boolean>, xValue: number, group: string, resultIfNoSamples: number|null)=>{
		resultIfNoSamples = null; // maybe temp; I actually like null to always be used for "no samples"

		const samples = sampleCollector.GetSampleValues(xValue, group);
		if (samples.length == 0) return resultIfNoSamples;
		return samples.filter(a=>a).length;
	}
	const getPercentTrue = (sampleCollector: MetricCollector<boolean>, xValue: number, group: string, resultIfNoSamples: number|null)=>{
		resultIfNoSamples = null; // maybe temp; I actually like null to always be used for "no samples"

		const samples = sampleCollector.GetSampleValues(xValue, group);
		if (samples.length == 0) return resultIfNoSamples;
		const hitCount = samples.filter(a=>a).length;
		return ((hitCount / samples.length) * 100).RoundTo(1);
	}

	let yValues: Array<number|n>;
	const xTicks_ext = xTicks_extendedForSmoothing; // just a shorter alias
	if (yType == "dreamSegments_sum") {
		yValues = xTicks_ext.map(x=>getSum(segmentExistenceSamples, x, group, 0));
	} else if (yType == "statNights_sum") {
		yValues = xTicks_ext.map(x=>{
			const nightSessionsForXValue = aggregationData.sessionsForEachXValue.get(x)?.filter(a=>a.launchType == "night") ?? [];
			const statNightCountForXValue = nightSessionsForXValue.map(a=>GetStatNightContainingX(a.startTime)).Distinct().length;
			return statNightCountForXValue;
		});
	} else if (yType == "lucids_perCycle") {
		yValues = xTicks_ext.map(x=>getAvgOfMidXPercent(segmentIsLucidSamples, x, group, 0, a=>a.RoundTo(.01)));
	} else if (yType == "lucids_perNight") {
		yValues = xTicks_ext.map(x=>{
			const nightSessionsForXValue = aggregationData.sessionsForEachXValue.get(x)?.filter(a=>a.launchType == "night") ?? [];
			const statNightCountForXValue = nightSessionsForXValue.map(a=>GetStatNightContainingX(a.startTime)).Distinct().length;
			const totalLucidValueForXValue = getSum(segmentIsLucidSamples, x, group, 0) ?? 0;
			if (statNightCountForXValue == 0) return null;
			return (totalLucidValueForXValue / statNightCountForXValue).RoundTo(.01);
		});
	} else if (yType == "lucids_sum") {
		yValues = xTicks_ext.map(x=>getSum(segmentIsLucidSamples, x, group, 0));
	} else if  (yType == "dreamsMatching_sum") {
		yValues = xTicks_ext.map(x=>getSum_boolean(segmentIsMatchSamples, x, group, 0));
	} else if (yType == "termsInShortText" || yType == "termsInLongText") {
		yValues = xTicks_ext.map(x=>getAvgOfMidXPercent(segmentTermCountSamples, x, group, 0, a=>a.RoundTo(1)));
	} else if (yType == "termsInShortText_sum" || yType == "termsInLongText_sum") {
		yValues = xTicks_ext.map(x=>getSum(segmentTermCountSamples, x, group, 0));
	} else if (yType == "responseTime") {
		yValues = xTicks_ext.map(x=>getAvgOfMidXPercent(responseTimeSamples, x, group, null, a=>(a / 1000).RoundTo(1)));
	} else if (yType == "linkVoicings_sum") {
		yValues = xTicks_ext.map(x=>linkVoicingExistenceSamples.GetSampleValues(x, group).length);
	} else if (yType == "linkVisualizations_sum") {
		yValues = xTicks_ext.map(x=>linkVisualizationExistenceSamples.GetSampleValues(x, group).length);
	} else if (yType == "realityChecks_sum") {
		yValues = xTicks_ext.map(x=>realityCheckExistenceSamples.GetSampleValues(x, group).length);
	} else if (yType == "frameDisplays_sum") {
		yValues = xTicks_ext.map(x=>frameDisplayExistenceSamples.GetSampleValues(x, group).length);
	} else if (yType == "visualizationPrompts_sum") {
		yValues = xTicks_ext.map(x=>visualizationPromptBlockSamples.GetSampleValues(x, group).Sum());
	} else if (yType == "quizPrompts_sum") {
		yValues = xTicks_ext.map(x=>quizPromptHasHitSamples.GetSampleValues(x, group).length);
	} /*else if (yType == "quizTryHitPercent") {
		yValues = xTicks_ext.map(xValue=>{
			const trySamples = quizTryIsHitSamples.GetSampleValues(xValue, group);
			if (trySamples.length == 0) return 0;
			const hitCount = trySamples.filter(a=>a).length;
			return ((hitCount / trySamples.length) * 100).RoundTo(1);
		});
	}*/ else if (yType == "quizFirstTryHitPercent") {
		yValues = xTicks_ext.map(x=>getPercentTrue(quizFirstTryIsHitSamples, x, group, null));
	} else if (yType == "quizTimeTillSuccess") {
		yValues = xTicks_ext.map(x=>getAvgOfMidXPercent(quizTimeTillSuccessSamples, x, group, null, a=>(a / 1000).RoundTo(.1)));
	} else if (yType == "memoryPalacePrompts_sum") {
		yValues = xTicks_ext.map(x=>memoryPalacePromptRecalledSamples.GetSampleValues(x, group).length);
	} else if (yType == "memoryPalacePromptRecallPercent") {
		yValues = xTicks_ext.map(x=>getPercentTrue(memoryPalacePromptRecalledSamples, x, group, null));
	} else if (yType == "memoryPalacePromptDecodeTime_median") {
		yValues = xTicks_ext.map(x=>getAvgOfMidXPercent(memoryPalacePromptDecodeTimeSamples, x, group, null, a=>(a / 1000).RoundTo(.1), 0));
	} else if (yType == "memoryPalacePromptEncodeTime_median") {
		yValues = xTicks_ext.map(x=>getAvgOfMidXPercent(memoryPalacePromptEncodeTimeSamples, x, group, null, a=>(a / 1000).RoundTo(.1), 0));
	} else if (yType == "mapObjects_sum_cumulative") {
		const latestStatDay_start = GetStatDayContainingX(Date.now());
		yValues = xTicks_ext.map(x=>{
			const cutoffTime = latestStatDay_start + (x + 1) * dayInMS;
			return aggregationData.mapObjects_all.filter(obj=>obj.createdAt < cutoffTime).length;
		});
	} else {
		if (!StatsYType_entries.includes(store.main.stats.view.yType as any)) {
			RunInAction("GetYValuesForYType_fixYType", ()=>store.main.stats.view.yType = StatsYType_entries[0]);
			Assert(false, `Invalid yType "${yType}". Resetting to first valid option "${StatsYType_entries[0]}".`);
		} else {
			AssertWarn(false, `Invalid yType "${yType}".`);
			return xTicks.map(()=>null);
		}
	}
	//console.log("YValues:", yValues, "aggData:", aggregationData);

	if (view.replaceYValNullWith != null) {
		yValues = yValues.map(val=>val == null ? view.replaceYValNullWith! : val);
	}

	const valMultiplier = GetValMultiplierForGroupMetricNormalization(view, group, yType);
	if (valMultiplier != 1) {
		yValues = yValues.map(val=>val == null ? val : (val * valMultiplier).RoundTo(1));
	}

	if (StatView.WillApplySmoothing(view)) {
		yValues = SmoothLine(yValues, view.smoothing ?? 0, view.smoothingType) as number[];
		// now that smoothing is done, trim the yValues to the correct/non-extended length
		yValues = yValues.slice(-xTicks.length);
	} else {
		Assert(xTicks_extendedForSmoothing.length == xTicks.length, "If not smoothing, xTicks_extendedForSmoothing should be same as xTicks.");
	}

	return yValues;
}

export function GetValMultiplierForGroupMetricNormalization(view: StatView, group: string, metric: StatsYType | "lucidityRate") {
	if (view.normalizeGroupMetrics && view.normalizeGroupMetrics_alarmDelay != null && view.grouping == StatsGrouping.alarmDelay) {
		const alarmDelayStandard = view.normalizeGroupMetrics_alarmDelay;
		const alarmDelayForCurrentGroup = Number(group);
		const metricMakesSenseToNormalize = ["termsInShortText", "termsInLongText", "lucidityRate"].includes(metric);
		if (metricMakesSenseToNormalize) {
			// if our wake-delay is 40mins, and the "standard" is 80mins, multiply our values by 2
			// (to normalize them, eg. so user can extrapolate results to "total amount expected per 8-hour night")
			const valMultiplier = alarmDelayStandard / alarmDelayForCurrentGroup;
			return valMultiplier;
		}
	}
	return 1;
}

export function SmoothLine(values: (number|n)[], smoothing: number, smoothType = SmoothingType.centered) {
	return values.map((value, index)=>{
		//if (values[i] == null) continue;
		if (value == null) return null;
		if (isNaN(value)) return null; // correct?
		const valuesToAverage = smoothType == SmoothingType.centered
			? values.slice((index - ((smoothing / 2) + (smoothing % 2))).KeepAtLeast(0), index + (smoothing / 2) + 1)
			: values.slice((index - smoothing).KeepAtLeast(0), index + 1);
		return (valuesToAverage.filter(a=>a != null && !isNaN(a) && a != Infinity && a != -Infinity) as number[]).Average();
	});
}